import { createContext, useCallback, useContext, useState, ReactNode } from 'react'

export type InstallSource = 'click' | 'hash'

interface ModalContextType {
  isOpen: boolean
  open: () => void
  close: () => void
  installOpen: boolean
  /** 'hash' = opened from the back-office link, no download has started. */
  installSource: InstallSource
  openInstall: (source: InstallSource) => void
  closeInstall: () => void
}

const ModalContext = createContext<ModalContextType | null>(null)

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [installOpen, setInstallOpen] = useState(false)
  // Stable so effects that subscribe with them (the #install-windows hash) don't re-run.
  const [installSource, setInstallSource] = useState<InstallSource>('click')
  const openInstall = useCallback((source: InstallSource) => {
    setInstallSource(source)
    setInstallOpen(true)
  }, [])
  const closeInstall = useCallback(() => setInstallOpen(false), [])

  return (
    <ModalContext.Provider
      value={{ isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false), installOpen, installSource, openInstall, closeInstall }}
    >
      {children}
    </ModalContext.Provider>
  )
}

export function useModal() {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('useModal must be used within ModalProvider')
  return ctx
}
